import { useContext, useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import clsx from "clsx";
import { match, P } from "ts-pattern";
import {
  Autocomplete,
  Button,
  Dialog,
  Input,
  SearchField,
} from "react-aria-components";

import DropdownPopover from "../dropdown-popover";
import Icon from "../icon";
import Caret from "../../icons/caret.svg?react";
import { PalletLangContext } from "../../lang-provider";

import ListBox from "./list-box";
import type { Item, ListBoxSelection } from "./list-box";
import Label from "./label";
import { selectedItems, subsetSelection } from "./select-state";
import type { SelectMsg, SelectState } from "./select-state";
import styles from "./select.module.css";

export interface SelectProps<T extends Item> {
  /** Plain data; the consumer builds it with a pure function. */
  state: SelectState<T>;
  /** Always required, even for `disabled` (which never emits). */
  onMsg: (msg: SelectMsg<T>) => void;
  display?: {
    trigger?: { minWidth?: string };
    menu?: {
      widthVariant?: "standard" | "match-trigger";
      maxHeight?: string;
    };
    // no `orientation`: it lives in `Label.visible`
  };
  className?: string;
  "data-testid"?: string;
}

/**
 * The one "select from these options" component: a value field (picked
 * labels, `(N)` count in `multiple`, `x`, caret) that opens a modal popover
 * holding an optional search box and a `ListBox`.
 *
 * A pure view: `state` in, `SelectMsg`s out (see `select-state.ts`). The
 * consumer owns selection AND search text and does all filtering; this
 * component renders `state.options` verbatim.
 *
 * The popover is rendered standalone rather than through `DialogTrigger`: the
 * open state is ours, and `DialogTrigger`'s press responder would make the
 * sibling clear button act as a trigger too.
 *
 * Search sits inside `Autocomplete`, not RAC's `ComboBox`: `ComboBox` is
 * single-select to its core, while `Autocomplete` wires an input to an
 * arbitrary collection, so the `ListBox` keeps `aria-multiselectable` and the
 * typeahead / `aria-activedescendant` wiring. No `filter` is passed.
 */
function Select<T extends Item>({
  state,
  onMsg,
  display,
  className,
  "data-testid": testId = "",
}: SelectProps<T>) {
  // Pallet owns open/closed and only REPORTS changes (`opened`/`closed`); the
  // consumer decides what a close means (usually: reset its search text).
  // Keep an eye on this as Select grows: if something outside the dropdown
  // ever needs to open/close it, or open state must survive a remount, move
  // `isOpen` into `SelectState` (consumer-owned).
  const [isOpen, setIsOpen] = useState(false);
  const open = isOpen && state.kind !== "disabled";
  useEffect(() => {
    // Re-enabling never pops open; no message (disabled never emits).
    if (state.kind === "disabled") setIsOpen(false);
  }, [state.kind]);
  const openPopover = () => {
    setIsOpen(true);
    onMsg({ kind: "opened" });
  };
  const close = () => {
    setIsOpen(false);
    onMsg({ kind: "closed" });
  };

  const lang = useContext(PalletLangContext);
  const clearLabel = lang?.simpleTranslate("select.clear") ?? "Clear selection";
  const expandLabel =
    lang?.simpleTranslate("select.expand") ?? "Show suggestions";
  const clearSearchLabel =
    lang?.simpleTranslate("actions.clearSearch") ?? "Clear search";

  const labelId = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const wasOpen = useRef(false);

  // A standalone controlled `Popover` does not hand focus back when it closes,
  // so do it here: only if focus is on the page or still inside the closing
  // popover (an outside press that landed on another control keeps its focus).
  useEffect(() => {
    if (wasOpen.current && !open) {
      const active = document.activeElement;
      if (
        !active ||
        active === document.body ||
        dialogRef.current?.contains(active)
      ) {
        valueRef.current?.focus();
      }
    }
    wasOpen.current = open;
  }, [open]);

  const labelEl = match(state.label)
    .with({ kind: "visible" }, ({ text, orientation }) => (
      <div className={styles.labelRow}>
        <Label id={labelId} display={{ orientation }}>
          {text}
        </Label>
      </div>
    ))
    .with({ kind: "aria" }, () => null)
    .exhaustive();
  const orientation =
    state.label.kind === "visible" ? state.label.orientation : "column";
  const nameProps =
    state.label.kind === "visible"
      ? { "aria-labelledby": labelId }
      : { "aria-label": state.label.text };

  const picked = selectedItems(state);
  const text =
    state.kind !== "disabled" && state.search.kind === "enabled"
      ? state.search.text
      : "";

  // RAC ListBox -> SelectMsg, per arm. Single/always: `empty` is a re-click on
  // the picked row, ignored (it neither deselects nor emits `picked`).
  const onSelectionChange = (sel: ListBoxSelection<T>) =>
    match(state)
      .with({ kind: "multiple" }, () =>
        onMsg({ kind: "pickedN", selection: sel }),
      )
      .with({ kind: P.union("single", "always") }, () => {
        const item =
          sel.kind === "subset" ? sel.items[sel.items.length - 1] : undefined;
        if (item) onMsg({ kind: "picked", item });
        close();
      })
      .with({ kind: "disabled" }, () => {})
      .exhaustive();
  const listSelection =
    state.kind === "multiple" ? state.selection : subsetSelection([...picked]);

  let menuDisplay = {};
  if (display?.menu?.maxHeight) {
    menuDisplay = { ...menuDisplay, maxHeight: display.menu.maxHeight };
  }
  if (display?.menu?.widthVariant) {
    menuDisplay = { ...menuDisplay, width: "100%", minWidth: "100%" };
  }

  const list = (items: ReadonlyArray<T>) => (
    <ListBox
      {...nameProps}
      items={[...items]}
      selection={listSelection}
      onSelectionChange={onSelectionChange}
      selectionMode={state.kind === "multiple" ? "multiple" : "single"}
      display={menuDisplay}
    />
  );

  const body: ReactNode = match(state)
    .with({ kind: "disabled" }, () => null) // popover never renders
    .with({ options: { kind: "loading" } }, ({ options }) => (
      <div className={styles.status} data-testid="select-loading">
        {options.label}
      </div>
    ))
    .with({ options: { kind: "ready" } }, ({ options }) =>
      options.items.length === 0 ? (
        <div className={styles.status} data-testid="select-empty">
          {options.emptyLabel}
        </div>
      ) : (
        list(options.items)
      ),
    )
    .with({ options: { kind: "fixed" } }, ({ options }) => list(options.items))
    .exhaustive();

  const search = state.kind === "disabled" ? undefined : state.search;

  const valueText = match(state)
    .with({ kind: "disabled" }, ({ reason }) => reason)
    .with({ kind: P.union("single", "multiple") }, ({ placeholder }) =>
      picked.length === 0 ? placeholder : picked.map((i) => i.label).join(", "),
    )
    .with({ kind: "always" }, () => picked.map((i) => i.label).join(", "))
    .exhaustive();
  const showPlaceholder = state.kind === "disabled" || picked.length === 0;

  return (
    <div
      data-testid={testId}
      className={clsx(styles.select, className, {
        [styles.row]: orientation === "row",
      })}
    >
      {labelEl}

      <div
        className={styles.field}
        ref={fieldRef}
        style={
          display?.trigger?.minWidth
            ? { minWidth: display.trigger.minWidth }
            : undefined
        }
      >
        <Button
          ref={valueRef}
          className={clsx(styles.value, {
            [styles.disabledReason]: state.kind === "disabled",
          })}
          isDisabled={state.kind === "disabled"}
          onPress={() => (open ? close() : openPopover())}
          aria-haspopup="dialog"
          aria-expanded={open}
          {...nameProps}
          data-testid="select-trigger"
        >
          <span
            className={clsx(styles.valueText, {
              [styles.placeholder]: showPlaceholder,
            })}
            data-testid="select-value"
          >
            {valueText}
          </span>
          {state.kind === "multiple" && picked.length > 0 && (
            <span className={styles.count} data-testid="select-count">
              ({picked.length})
            </span>
          )}
        </Button>
        {(state.kind === "single" || state.kind === "multiple") &&
          picked.length > 0 && (
            <Button
              className={styles.clear}
              aria-label={clearLabel}
              // Reachable by pointer and screen reader, skipped when tabbing: it
              // undoes a value the field already shows, and a tab stop here
              // would sit in the way of every keyboard user only opening the list.
              excludeFromTabOrder
              onPress={() => onMsg({ kind: "cleared" })}
              data-testid="select-clear"
            >
              <Icon name="X" size={16} />
            </Button>
          )}
        <Button
          className={styles.caret}
          aria-label={expandLabel}
          excludeFromTabOrder
          isDisabled={state.kind === "disabled"}
          onPress={() => (open ? close() : openPopover())}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid="select-caret"
        >
          <Caret aria-hidden="true" />
        </Button>
      </div>

      <DropdownPopover
        triggerRef={fieldRef}
        isOpen={open}
        onOpenChange={(next) => (next ? openPopover() : close())}
        className={clsx({
          [styles.matchTrigger]:
            display?.menu?.widthVariant === "match-trigger",
        })}
      >
        <Dialog ref={dialogRef} aria-label={state.label.text}>
          <div
            // Once an option has been picked, `Autocomplete` forwards keys to the
            // option holding virtual focus and Escape never reaches the popover's
            // own dismissal. So close on it here, in the capture phase. While
            // text is typed, leave the first Escape to `SearchField`, which
            // spends it clearing that text.
            onKeyDownCapture={(event) => {
              if (event.key === "Escape" && text === "") {
                event.preventDefault();
                event.stopPropagation();
                close();
              }
            }}
          >
            {search?.kind === "enabled" ? (
              <Autocomplete
                inputValue={search.text}
                onInputChange={(t) => onMsg({ kind: "searched", text: t })}
              >
                <SearchField
                  aria-label={search.placeholder}
                  className={styles.searchBox}
                  autoFocus
                >
                  <Input
                    data-testid="select-search-input"
                    autoComplete="off"
                    placeholder={search.placeholder}
                    className={styles.input}
                  />
                  {/* Named from `PalletLangContext`, not react-aria's bundled
                    intl: the glyph alone is not an accessible name. */}
                  <Button
                    aria-label={clearSearchLabel}
                    onPress={() => onMsg({ kind: "searched", text: "" })}
                    className={clsx(styles.clearButton, {
                      [styles.hideButton]: text === "",
                    })}
                  >
                    ✕
                  </Button>
                </SearchField>
                {body}
              </Autocomplete>
            ) : (
              body
            )}
          </div>
        </Dialog>
      </DropdownPopover>
    </div>
  );
}

export default Select;
